// context/CameraContext.jsx
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const CameraContext = createContext();

export const useCamera = () => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error('useCamera must be used within CameraProvider');
  }
  return context;
};

export const CameraProvider = ({ children }) => {
  const [stream, setStream] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const [deviceCapabilities, setDeviceCapabilities] = useState({
    hasCamera: false,
    hasTorch: false,
    hasDepthSensor: false,
    deviceType: 'unknown'
  });
  
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const streamRef = useRef(null);
  const primaryVideoRef = useRef(null);
  const primaryVideoListenersRef = useRef({ videoEl: null, onLoadedMeta: null, onCanPlay: null });
  const playWarnedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        stopCamera();
      }
    };
  }, []);

  /**
   * Rileva le capacità del dispositivo.
   *
   * FIX: non apre più uno stream di test con getUserMedia.
   * Il vecchio approccio causava NotReadableError su mobile: track.stop() non
   * rilascia il lock hardware istantaneamente, e startCamera() chiamato subito
   * dopo trovava la camera ancora occupata — anche con delay.
   *
   * Strategia attuale:
   * - Se uno stream principale è già attivo (streamRef), legge le capabilities
   *   direttamente dal track esistente — zero overhead.
   * - Se non c'è stream attivo, usa solo enumerateDevices() + label heuristics
   *   per torch/depth: nessun getUserMedia, nessun lock camera.
   * - Il rilevamento torch preciso avviene la prima volta che startCamera()
   *   acquisisce lo stream reale (vedi updateCapabilitiesFromTrack).
   */
  const detectCapabilities = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      const hasCamera = videoDevices.length > 0;

      if (!hasCamera) {
        setDeviceCapabilities(prev => ({ ...prev, hasCamera: false }));
        return false;
      }

      // Se lo stream principale è già attivo, leggi le capabilities da lì
      if (streamRef.current) {
        const track = streamRef.current.getVideoTracks()[0];
        const capabilities = track?.getCapabilities ? track.getCapabilities() : {};
        const caps = {
          hasCamera: true,
          hasTorch: !!capabilities.torch,
          hasDepthSensor: await checkDepthSensor(),
          deviceType: detectDeviceType(),
        };
        setDeviceCapabilities(caps);
        return caps;
      }

      // Nessuno stream attivo: usa solo enumerateDevices, nessun getUserMedia.
      // hasTorch sarà aggiornato la prima volta che startCamera() gira.
      const hasDepthByLabel = videoDevices.some(d =>
        d.label.toLowerCase().includes('depth') ||
        d.label.toLowerCase().includes('tof')
      );

      const caps = {
        hasCamera: true,
        hasTorch: false,           // aggiornato da updateCapabilitiesFromTrack() in startCamera
        hasDepthSensor: hasDepthByLabel || await checkDepthSensor(),
        deviceType: detectDeviceType(),
      };

      setDeviceCapabilities(caps);
      return caps;
    } catch (err) {
      console.error('Error detecting capabilities:', err);
      setError('Failed to detect device capabilities');
      return null;
    }
  };

  /**
   * Aggiorna hasTorch dopo che lo stream reale è stato acquisito.
   * Chiamato internamente da startCamera() una volta che il track è disponibile.
   */
  const updateCapabilitiesFromTrack = (track) => {
    if (!track) return;
    const capabilities = track.getCapabilities ? track.getCapabilities() : {};
    if (capabilities.torch !== undefined) {
      setDeviceCapabilities(prev => ({ ...prev, hasTorch: !!capabilities.torch }));
    }
  };

  /**
   * Check for depth sensor availability
   */
  const checkDepthSensor = async () => {
    // iOS LiDAR check
    if (window.webkit?.messageHandlers?.depthCapture) {
      return true;
    }

    // WebXR depth check
    if ('XRSession' in window && navigator.xr) {
      try {
        const isSupported = await navigator.xr.isSessionSupported('immersive-ar');
        if (isSupported) {
          return true;
        }
      } catch (e) {
        // WebXR not available
      }
    }

    // Android depth camera check
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some(device => 
        device.kind === 'videoinput' && 
        (device.label.toLowerCase().includes('depth') || 
         device.label.toLowerCase().includes('tof'))
      );
    } catch (e) {
      return false;
    }
  };

  /**
   * Detect device type
   */
  const detectDeviceType = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'iOS';
    }
    
    if (/android/i.test(userAgent)) {
      return 'Android';
    }
    
    return 'Desktop';
  };

  /**
   * Start camera with single stream for all components
   */
  const startCamera = async () => {
    // If camera is already active, return existing stream
    if (streamRef.current && isActive) {
      console.log('Camera already active, returning existing stream');
      return streamRef.current;
    }

    try {
      setError(null);
      
      // First detect capabilities if not done
      if (!deviceCapabilities.hasCamera) {
        await detectCapabilities();
      }

      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 },
          frameRate: { ideal: 30, min: 15 }
        }
      };

      // Add audio for future features
      constraints.audio = false;

      console.log('Starting camera with constraints:', constraints);
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      const videoTrack = newStream.getVideoTracks()[0];

      // Aggiorna hasTorch ora che abbiamo il track reale
      updateCapabilitiesFromTrack(videoTrack);
      
      // Store references
      streamRef.current = newStream;
      trackRef.current = videoTrack;
      
      setStream(newStream);
      setIsActive(true);

      console.log('Camera stream ready');
      return newStream;
    } catch (err) {
      console.error('Failed to start camera:', err);
      
      let errorMessage = 'Failed to access camera';
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera access.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      }
      
      setError(errorMessage);
      setIsActive(false);
      throw new Error(errorMessage);
    }
  };

  /**
   * Stop camera and cleanup
   */
  const stopCamera = () => {
    console.log('Stopping camera');
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Track stopped:', track.kind);
      });
      streamRef.current = null;
    }
    
    setStream(null);
    setIsActive(false);
    trackRef.current = null;
    
    console.log('Camera stopped');
  };

  /**
   * Attach stream to video element
   */
  const attachToVideo = async (videoElement) => {
    if (!videoElement) return;
    
    if (streamRef.current && isActive) {
      if (videoElement.srcObject === streamRef.current) {
        return;
      }

      console.log("CameraContext: attaching stream to video");

      videoElement.muted = true;
      videoElement.playsInline = true;
      videoElement.autoplay = true;

      videoElement.srcObject = streamRef.current;
      videoRef.current = videoElement;
      
      try {
        await videoElement.play();
      } catch (err) {
        if (!playWarnedRef.current) {
          playWarnedRef.current = true;
          console.warn("Failed to play video:", err);
        }
      }
    }
  };

  /**
   * Set/get the "primary" video element used across the pipeline
   */
  const setPrimaryVideoEl = (videoElement) => {
    const prev = primaryVideoRef.current;
    if (prev === videoElement) return;

    primaryVideoRef.current = videoElement || null;
    console.log("CameraContext: primary video set", !!videoElement);

    const listeners = primaryVideoListenersRef.current;
    if (listeners.videoEl && listeners.onLoadedMeta) {
      try { listeners.videoEl.removeEventListener("loadedmetadata", listeners.onLoadedMeta); } catch {}
    }
    if (listeners.videoEl && listeners.onCanPlay) {
      try { listeners.videoEl.removeEventListener("canplay", listeners.onCanPlay); } catch {}
    }

    listeners.videoEl = videoElement || null;
    listeners.onLoadedMeta = null;
    listeners.onCanPlay = null;

    if (videoElement) {
      const onLoadedMeta = () => {
        console.log("CameraContext: primary video loadedmetadata", {
          w: videoElement.videoWidth,
          h: videoElement.videoHeight,
          rs: videoElement.readyState,
        });
      };
      const onCanPlay = () => {
        console.log("CameraContext: primary video canplay", {
          w: videoElement.videoWidth,
          h: videoElement.videoHeight,
          rs: videoElement.readyState,
        });
      };
      listeners.onLoadedMeta = onLoadedMeta;
      listeners.onCanPlay = onCanPlay;
      videoElement.addEventListener("loadedmetadata", onLoadedMeta);
      videoElement.addEventListener("canplay", onCanPlay);
    }
  };

  const isVideoReady = (videoElement) => {
    return !!videoElement && videoElement.readyState >= 2 && videoElement.videoWidth > 0 && videoElement.videoHeight > 0;
  };

  /**
   * Get current video track
   */
  const getVideoTrack = () => {
    return trackRef.current;
  };

  /**
   * Take snapshot from video
   */
  const takeSnapshot = () => {
    if (!videoRef.current || !videoRef.current.videoWidth) return null;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    return canvas;
  };

  const value = {
    // State
    stream,
    isActive,
    error,
    deviceCapabilities,
    
    // Refs
    videoRef,
    primaryVideoRef,
    trackRef,
    
    // Methods
    startCamera,
    stopCamera,
    attachToVideo,
    setPrimaryVideoEl,
    isVideoReady,
    getVideoTrack,
    takeSnapshot,
    detectCapabilities
  };

  return (
    <CameraContext.Provider value={value}>
      {children}
    </CameraContext.Provider>
  );
};
